import { useParams } from 'react-router-dom';

import { useApplicantVendorStatusQuery } from '@/components/shared/api/queries/applicant';
import { TabDataState } from '@/components/shared/components/TabDataState';

import {
    DDDCardHeader,
    DDDCardSection,
    DDDCardSectionTitle,
    DDDCardTitle,
} from '../../Applicant.StyledComponents';
import { getDddBlockAnchorId } from '../dddAnchors';

import {
    GroupReason,
    GroupTable,
    GroupTableWrapper,
    OwnerSubgroup,
    OwnerSubgroups,
    OwnerTitle,
    StatusCell,
    StatusPill,
    VendorStatusCard,
    VendorStatusCell,
    VendorStatusDivider,
    VendorStatusGroupBody,
    VendorStatusHeaderCell,
    VendorStatusRowItem,
    VendorStatusWrapper,
    ViewDddLink,
} from './VendorStatusTable.StyledComponents';
import { VendorStatus, VendorStatusGroup, VendorStatusRow } from './VendorStatusTable.types';
import {
    getStatusLabel,
    getStatusSentence,
    getStatusVariant,
    getTimingCell,
    hasRecentPendingRow,
} from './VendorStatusTable.utils';

const PENDING_POLL_MS = 30_000;

const LINKED_STATUSES = new Set([VendorStatus.Completed, VendorStatus.Rejected]);

interface VendorStatusTableProps {
    // The ids actually rendered by the sibling block list, so a row can only offer a link that resolves.
    blockAnchorIds?: Set<string>;
    idPrefix?: string;
    // Called before scrolling, so the sibling list can expand a collapsed block first.
    onViewDdd?: (anchorId: string) => void;
}

export const VendorStatusTable = ({
    blockAnchorIds,
    idPrefix = '',
    onViewDdd,
}: VendorStatusTableProps = {}) => {
    const { applicantId } = useParams();

    const {
        data: vendorStatus,
        dataUpdatedAt,
        isLoading,
        isLoadingError,
        refetch,
    } = useApplicantVendorStatusQuery({
        applicantId: applicantId ?? '',
        // A pending row settles by Alloy webhook, which nothing else on this page picks up. Matches the
        // Activity tab's cadence, and stops once every row has settled or been pending over an hour.
        refetchInterval: (query) =>
            hasRecentPendingRow(query.state.data, Date.now()) ? PENDING_POLL_MS : false,
    });

    // Waiting times count to the last fetch, so each poll advances the clock with the data.
    const now = dataUpdatedAt;

    const groups = vendorStatus?.groups ?? [];

    // An Errored row is deliberately left without a link: its block, when one exists at all, holds the
    // previous successful pull and would contradict the status beside it. A Rejected row's block is
    // that rejected attempt, so it links.
    const renderViewDddLink = (row: VendorStatusRow, ownerId?: string | null) => {
        if (!LINKED_STATUSES.has(row.status)) {
            return null;
        }

        const anchorId = getDddBlockAnchorId(row.vendor, ownerId, idPrefix);

        if (!anchorId || !blockAnchorIds?.has(anchorId)) {
            return null;
        }

        return (
            <ViewDddLink
                href={`#${anchorId}`}
                data-testid={`view-ddd-link-${anchorId}`}
                onClick={(event) => {
                    // Scroll without the href appending a hash the applicant page would then carry around.
                    event.preventDefault();
                    onViewDdd?.(anchorId);
                    document
                        .getElementById(anchorId)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
            >
                View DDD
            </ViewDddLink>
        );
    };

    const renderVendorTable = (rows: VendorStatusRow[], ownerId?: string | null) => (
        <GroupTableWrapper>
            <GroupTable>
                <thead>
                    <tr>
                        <VendorStatusHeaderCell>Vendor</VendorStatusHeaderCell>
                        <VendorStatusHeaderCell>Status</VendorStatusHeaderCell>
                        <VendorStatusHeaderCell>Timing</VendorStatusHeaderCell>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <VendorStatusRowItem
                            key={row.vendor}
                            data-testid={`vendor-status-row-${row.vendor}`}
                        >
                            <VendorStatusCell>{row.vendor}</VendorStatusCell>
                            <VendorStatusCell>
                                <StatusCell>
                                    <StatusPill variant={getStatusVariant(row.status)}>
                                        {getStatusLabel(row.status)}
                                    </StatusPill>
                                    <span>{getStatusSentence(row)}</span>
                                    {renderViewDddLink(row, ownerId)}
                                </StatusCell>
                            </VendorStatusCell>
                            <VendorStatusCell>{getTimingCell(row, now)}</VendorStatusCell>
                        </VendorStatusRowItem>
                    ))}
                </tbody>
            </GroupTable>
        </GroupTableWrapper>
    );

    const renderGroup = (group: VendorStatusGroup) => {
        const subgroups = group.subgroups ?? [];

        return (
            <DDDCardSection key={group.title} data-testid="vendor-status-group">
                <DDDCardSectionTitle>{group.title}</DDDCardSectionTitle>
                <VendorStatusGroupBody>
                    {group.groupReason ? <GroupReason>{group.groupReason}</GroupReason> : null}
                    {subgroups.length > 0 ? (
                        <OwnerSubgroups>
                            {subgroups.map((subgroup) => (
                                <OwnerSubgroup
                                    key={subgroup.ownerId ?? subgroup.title}
                                    data-testid={`vendor-status-subgroup-${subgroup.title}`}
                                >
                                    <OwnerTitle>{subgroup.title}</OwnerTitle>
                                    {renderVendorTable(subgroup.rows, subgroup.ownerId)}
                                </OwnerSubgroup>
                            ))}
                        </OwnerSubgroups>
                    ) : (
                        renderVendorTable(group.rows)
                    )}
                </VendorStatusGroupBody>
            </DDDCardSection>
        );
    };

    if (!applicantId) {
        return <></>;
    }

    return (
        <VendorStatusWrapper>
            <TabDataState
                isLoading={isLoading}
                isLoadingError={isLoadingError}
                onRetry={() => refetch()}
                isEmpty={groups.length === 0}
                emptyMessage="No vendor status found for applicant"
                errorMessage="Could not load vendor status."
            >
                <VendorStatusCard>
                    <DDDCardHeader>
                        <DDDCardTitle>Data Source Status</DDDCardTitle>
                    </DDDCardHeader>
                    {groups.map(renderGroup)}
                </VendorStatusCard>
                <VendorStatusDivider />
            </TabDataState>
        </VendorStatusWrapper>
    );
};
